import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  Renderer2,
} from '@angular/core';
import { NgControl } from '@angular/forms';

/**
 * 身分證input directive
 * 1. 禁止輸入非英文/數字字符
 * 2. 用戶輸入<input>身份證號後5碼隱碼(*)
 */
@Directive({
  selector: '[appMaskIdNumber]',
  standalone: true,
})
export class MaskIdNumberDirective implements AfterViewInit, OnChanges {
  @Input('maskEnabled') maskEnabled: boolean = true; // 控制是否啟用遮罩
  
  private actualValue: string = '';
  private isProcessing: boolean = false; // 防止重複處理

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngControl: NgControl
  ) {}

  ngOnChanges() {
    // 當 maskEnabled 改變時，更新顯示值
    this.updateDisplayValue();
  }

  // 刪除所有非英文和數字字符
  private sanitizeAlphaNumeric(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  // 身分證第一碼為英文字母，無條件轉大寫
  private uppercaseFirstLetter(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    // 防止重複處理
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    const inputElement = event.target as HTMLInputElement;
    let inputValue = inputElement.value;
    const originalValue = inputValue;

    // 遮罩未啟用
    if (!this.maskEnabled) {
      inputValue = this.uppercaseFirstLetter(this.sanitizeAlphaNumeric(inputValue));
      this.actualValue = inputValue;
      this.ngControl.control?.setValue(inputValue, { emitEvent: false });
      
      // 如果值有變化，更新 DOM 並觸發事件
      if (originalValue !== inputValue) {
        this.renderer.setProperty(inputElement, 'value', inputValue);
        // 觸發 Angular 的變更檢測
        inputElement.dispatchEvent(new Event('input'));
      }
      
      this.isProcessing = false;
      return;
    }

    const cursorPosition = inputElement.selectionStart;

    this.updateActualValue(inputValue, cursorPosition ?? 0);
    this.actualValue = this.uppercaseFirstLetter(
      this.sanitizeAlphaNumeric(this.actualValue)
    );
    this.ngControl.control?.setValue(this.actualValue, { emitEvent: false });
    
    const maskedValue = this.getMaskedValue(this.actualValue);
    this.renderer.setProperty(inputElement, 'value', maskedValue);

    setTimeout(() => {
      inputElement.setSelectionRange(cursorPosition, cursorPosition);
      this.isProcessing = false;
    }, 0);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (!this.maskEnabled) return;

    const inputElement = event.target as HTMLInputElement;
    const selectionStart = inputElement.selectionStart || 0;
    const selectionEnd = inputElement.selectionEnd || 0;

    // 全選刪除全部的情況
    if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      selectionStart === 0 &&
      selectionEnd === this.actualValue.length &&
      this.actualValue.length > 0
    ) {
      this.actualValue = '';
      return;
    }

    // 點擊Backspace
    if (
      event.key === 'Backspace' &&
      selectionStart > 0 &&
      selectionStart === selectionEnd
    ) {
      this.actualValue =
        this.actualValue.slice(0, selectionStart - 1) +
        this.actualValue.slice(selectionStart);
    }
    // 點擊Delete
    else if (
      event.key === 'Delete' &&
      selectionStart < this.actualValue.length &&
      selectionStart === selectionEnd
    ) {
      this.actualValue =
        this.actualValue.slice(0, selectionStart) +
        this.actualValue.slice(selectionStart + 1);
    }
    // 刪除部分字元（有選取範圍）
    else if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      selectionStart !== selectionEnd
    ) {
      this.actualValue =
        this.actualValue.slice(0, selectionStart) +
        this.actualValue.slice(selectionEnd);
    }
  }

  private updateActualValue(inputValue: string, cursorPosition: number) {
    // 找出新輸入的字符
    const maskedValue = this.getMaskedValue(this.actualValue);

    if (inputValue.length > maskedValue.length) {
      // 有新字符輸入
      const newChar = inputValue.charAt(cursorPosition - 1);
      if (newChar !== '*') {
        // 在指定位置插入新字符
        this.actualValue =
          this.actualValue.slice(0, cursorPosition - 1) +
          newChar +
          this.actualValue.slice(cursorPosition - 1);
      }
    }

    // 限制長度為10
    if (this.actualValue.length > 10) {
      this.actualValue = this.actualValue.slice(0, 10);
    }
  }

  // 隱碼後顯示的值
  private getMaskedValue(value: string): string {
    if (!value || !this.maskEnabled) {
      return value || '';
    }

    const str = value.toString();

    if (str.length <= 5) {
      return str;
    } else if (str.length < 10) {
      return str.substring(0, 5) + '*'.repeat(str.length - 5);
    } else {
      return str.substring(0, 5) + '*****';
    }
  }

  private updateDisplayValue() {
    this.actualValue = this.uppercaseFirstLetter(this.ngControl.value || '');

    const displayValue = this.getMaskedValue(this.actualValue);
    this.renderer.setProperty(this.el.nativeElement, 'value', displayValue);
  }

  // 初始化時設定遮罩值
  ngAfterViewInit() {
    const initialValue = this.ngControl.value || '';
    if (initialValue) {
      this.actualValue = initialValue;
      this.updateDisplayValue();
    }
  }
}
